import { IPromise, IAngularEvent, IQService } from "angular";
import { IModalScope } from "angular-ui-bootstrap";
import { HotkeysProvider } from "angular-hotkeys";
import dayjs from "@gtmhub/dayjs";
import { IGtmhubRootScopeService } from "@gtmhub/models";
import { IGoal, ICheckinComment } from "@gtmhub/goals/models";
import { IAssignee } from "@gtmhub/assignees/models";
import { GoalService } from "@gtmhub/goals/goalService";
import { PermissionsService } from "@gtmhub/users/permissionsService";
import { CommentService, IComment, IGif } from "@gtmhub/comments";
import { ITeam, ITeamsStoreState, TeamsActions } from "@gtmhub/teams";
import { CfMap } from "@gtmhub/customFields";
import { customNotFoundError, IUIError, UIErrorHandlingService } from "@gtmhub/error-handling";
import { IMetric, ICheckin, MetricService } from "..";
import { INgRedux } from "@gtmhub/state-management";
import { AssigneeActions, IAssigneesStoreState } from "@gtmhub/assignees";
import { IStateInit } from "@gtmhub/core/routing";
import { IExtendedRootScopeService } from "@gtmhub/shared/ng-extensions";
import { ISessionsStoreState, IPlanningSession, PlanningSessionsActions } from "@gtmhub/sessions";
import { storage } from "@gtmhub/core/storage";
import { ITraceRootScopeService } from "@gtmhub/core/tracing";
import { getCurrentUserId, getCurrentUserRoles } from "@gtmhub/users";
import { IdMap } from "@gtmhub/util";
import { SizeOfGif } from "@gtmhub/shared/services/tenor-gif.service";
import { OkrEvents } from "@gtmhub/okrs";

interface IUpdateMetricRedux {
  reduxItemsFetched: boolean;
  reduxItemsError: IUIError;
  assignees: IdMap<IAssignee>;
  teams: IdMap<ITeam>;
  sessions: IPlanningSession[];
}

const bindStateToScope = (state: IAssigneesStoreState & ISessionsStoreState & ITeamsStoreState): IUpdateMetricRedux => ({
  assignees: state.assignees.map,
  sessions: state.sessions.items,
  teams: state.teams.map,
  reduxItemsFetched: state.assignees.isFetched && state.sessions.isFetched && state.teams.isFetched,
  reduxItemsError: state.assignees.error || state.sessions.error || state.teams.error,
});

export interface IUpdateMetricParams {
  metricId: string;
  checkinId: string;
  editOn: boolean;
}

export interface IUpdateMetricScope extends IModalScope, IExtendedRootScopeService, IUpdateMetricRedux {
  sizeOfGif: typeof SizeOfGif;
  // to do: add ctrl specific interface/type fetched https://gtmhub.atlassian.net/browse/GVS-10053
  metric: IMetric;
  params: IUpdateMetricParams;
  userId: string;
  goal: IGoal; // inherited from the parent state
  uiMode: string;
  allowedActions: string[];
  datepickerOptions: {
    minDate: Date;
    maxDate: Date;
  };
  customCheckin: boolean;
  planningSession: IPlanningSession;
  answers: string[];
  checkin: ICheckin;
  currentDate: string;
  mentionIds: string[];
  mentionsPreloaded: string[];
  nothingChangedReason: string;
  indicators: {
    loading: boolean;
    loadingActions: boolean;
    saving: boolean;
  };
  requiredCfNotEmpty: boolean;
  removeMention(id: string): void;
  removeAllMentions(): void;
  changeReason(answer: string): void;
  changeUIMode(uiMode: string): void;
  save(): void;
  updateMetricComment(checkinId: string, comment: string): void;
  validateData(firstDate?: string, secondDate?: string, strict?: boolean, unit?: string): boolean;
  updateBooleanMetric(): void;
  updateCustomFields(updateCustomFields: CfMap): void;
  setRequiredCf(requiredCf: { areFilled: boolean }): void;
  setNewCommentGif(gif: IGif): void;
}

const cachedUpdateMetricMap: Map<string, IMetric> = new Map();
let currentMetricId: string = null;
let cacheTimestamp: number = Date.now();
const isCacheTimedout = (): boolean => {
  const cacheTimeout = 5 * 60 * 1000; // 5 minuts in ms
  return Date.now() - cacheTimestamp > cacheTimeout;
};
export class UpdateMetricCtrl implements IStateInit {
  public static $inject = [
    "$rootScope",
    "$scope",
    "$q",
    "$stateParams",
    "GoalService",
    "MetricService",
    "PermissionsService",
    "CommentService",
    "hotkeys",
    "UIErrorHandlingService",
    "AssigneeActions",
    "PlanningSessionsActions",
    "$ngRedux",
    "TeamsActions",
  ];

  private okrEvents: OkrEvents;

  constructor(
    private $rootScope: IGtmhubRootScopeService & ITraceRootScopeService,
    private $scope: IUpdateMetricScope,
    private $q: IQService,
    private $stateParams: IUpdateMetricParams,
    private goalService: GoalService,
    private metricService: MetricService,
    private permissionsService: PermissionsService,
    private commentService: CommentService,
    private hotkeys: HotkeysProvider,
    private uiErrorHandlingService: UIErrorHandlingService,
    private assigneeActions: AssigneeActions,
    private sessionActions: PlanningSessionsActions,
    private $ngRedux: INgRedux,
    private teamsActions: TeamsActions
  ) {
    this.okrEvents = new OkrEvents(this.$scope);

    this.$scope.indicators = {
      loading: true,
      saving: false,
      loadingActions: false,
    };

    this.okrEvents = new OkrEvents(this.$scope);
    this.$scope.mentionsPreloaded = [];
    this.$scope.sizeOfGif = SizeOfGif;
    this.$scope.removeMention = this.removeMention;
    this.$scope.removeAllMentions = this.removeAllMentions;
    this.$scope.planningSession = {};
    this.$scope.params = { ...this.$stateParams };
    this.$scope.uiMode = "changed";
    this.$scope.updateMetricComment = this.updateMetricComment;
    this.$scope.validateData = this.validateData;
    this.$scope.setNewCommentGif = this.setNewCommentGif;

    // methods mapping
    this.$scope.save = this.save;
    this.$scope.changeUIMode = this.changeUIMode;
    this.$scope.changeReason = this.changeReason;

    this.$scope.updateBooleanMetric = this.updateBooleanMetric;
    this.$scope.updateCustomFields = this.updateCustomFields;
    this.$scope.setRequiredCf = this.setRequiredCf;

    this.$scope.$watch<string>("checkin.comment", (value) => {
      // commentValue is used to store the value for the removeMention method
      this.commentValue = value;
      this.onCommentChange(value);
    });

    this.$scope.userId = getCurrentUserId();
    this.$scope.datepickerOptions = {
      minDate: new Date(),
      maxDate: new Date(),
    };
  }

  private onExit(event: IAngularEvent): void {
    // We want to save the state of the modal only if it was closed with backdrop
    // We consider this as accidental form close and we want to save the state of the form
    const modalCloseReason = String(storage.get("modalCloseReason"));
    if (modalCloseReason && modalCloseReason.includes("backdrop")) {
      cacheTimestamp = Date.now();
      cachedUpdateMetricMap[currentMetricId] = (event.currentScope as IUpdateMetricScope).checkin;
    } else {
      cachedUpdateMetricMap[currentMetricId] = null;
    }
  }

  public stateInit(): IPromise<unknown> {
    this.isAdmin();

    const unsubscribe = this.$ngRedux.connect(bindStateToScope)(this.$scope);
    this.$scope.$on("$destroy", unsubscribe);
    this.$scope.$on("$destroy", this.onExit);

    this.$ngRedux.dispatch(this.assigneeActions.getAssignees());
    this.$ngRedux.dispatch(this.sessionActions.getSessionsIfMissing());
    this.$ngRedux.dispatch(this.teamsActions.fetchTeamsIfMissing());

    return this.$scope
      .watchUntilAndRejectOnError(
        () => this.$scope.reduxItemsFetched,
        () => this.$scope.reduxItemsError
      )
      .then(() => {
        currentMetricId = this.$stateParams.metricId;
        this.getMetricActions(this.$stateParams.metricId);
        return this.init();
      })
      .catch((error) => this.uiErrorHandlingService.handleIndicator(error, { indicator: "loading" }));
  }

  private allUserOwnerIds: string[];
  private commentValue: string;
  private isCurrentUserAdmin: boolean;

  private init = (): IPromise<unknown> => {
    return this.getMetric(this.$scope.params.metricId).then(
      (metric) => {
        this.$scope.metric = metric;

        if (this.$stateParams.editOn === true) {
          const checkinId = this.$scope.params.checkinId;

          for (const snapshot of this.$scope.metric.metricHistory) {
            if (checkinId === snapshot.id) {
              this.$scope.checkin = snapshot;
              break;
            }
          }
        } else {
          this.$scope.answers = ["I haven't worked on this", "This is not a priority", "Other"];

          this.$scope.nothingChangedReason = this.$scope.answers[0];

          this.hotkeys.add({
            combo: "shift+a",
            callback: () => {
              this.$scope.datepickerOptions.maxDate = new Date("2038-04-03T08:58:37.791Z");
            },
          });

          this.processMetric(this.$scope.metric);

          if (this.$scope.metric.insightName !== "") {
            this.changeUIMode("changed");
          }
        }

        if (cachedUpdateMetricMap[currentMetricId] && !isCacheTimedout()) {
          this.$scope.checkin = cachedUpdateMetricMap[currentMetricId];
        }

        const goalFields = "id,sessionId";

        this.addOwnersToMentioned();

        return this.goalService.getGoalWithFields(this.$scope.metric.goalId, goalFields).then((goal) => {
          this.$scope.indicators.loading = false;

          this.$scope.goal = goal;

          this.$scope.planningSession = this.$scope.sessions.find((s) => s.id === this.$scope.goal.sessionId);

          this.adjustDates(this.$scope.planningSession);

          if (this.$scope.metric.insightName === "" && !this.$scope.metric.cascadeType) {
            this.$scope.$broadcast("focusTextInput");
          }
        });
      },
      (err: IUIError) => {
        this.uiErrorHandlingService.handleIndicator(err, { indicator: "loading" });
      }
    );
  };

  private getMetric = (metricId: string): IPromise<IMetric> => {
    // to do: add field projection with https://gtmhub.atlassian.net/browse/GVS-10053
    const filter = { _id: metricId };
    return this.metricService.getMetricsV2({ filter }).then((collection) => {
      if (collection.items.length) {
        return collection.items[0];
      } else {
        return this.$q.reject(customNotFoundError());
      }
    });
  };

  private setNewCommentGif = (gif: IGif): void => {
    this.$scope.checkin.gif = gif;
  };

  private addOwnersToMentioned = (): void => {
    this.allUserOwnerIds = [
      ...this.$scope.metric.ownerIds.reduce((ids: Set<string>, id: string) => {
        const owner = this.$scope.assignees[id];
        if (owner) {
          if (owner.type === "user") {
            ids.add(id);
          } else {
            const teamOwner = this.$scope.teams[id];

            if (teamOwner) {
              if (teamOwner.members) {
                for (const memberId of teamOwner.members) {
                  ids.add(memberId);
                }
              }

              if (teamOwner.manager) {
                ids.add(teamOwner.manager);
              }
            }
          }
        }
        return ids;
      }, new Set()),
    ];

    this.addMention(this.allUserOwnerIds);
  };

  private getMetricActions = (metricId: string, type = "metric"): void => {
    this.$scope.indicators.loadingActions = true;

    this.permissionsService.getCurrentUserAllowedActionsPerItem(metricId, type).then(
      (actions) => {
        this.$scope.indicators.loadingActions = false;
        this.$scope.allowedActions = actions.currentUserActions;
      },
      (err) => this.handleIndicatorError(err, "loadingActions", false)
    );
  };

  private isAdmin = () => {
    const roles = getCurrentUserRoles();
    this.isCurrentUserAdmin = roles.includes("admin");
  };

  private onCommentChange = (value: string) => {
    let result = [];
    const ids = [];
    if (value) {
      const re = /@\[(.*?)\]/g;
      result = value.match(re);
    }
    if (result && result.length > 0) {
      for (let i = 0; i < result.length; i++) {
        const item = result[i].split(":")[2];
        const id = item.substring(0, item.length - 1);
        // this prevents the visual repetition of avatars in mentions list
        if (this.$scope.mentionsPreloaded.indexOf(id) === -1) {
          ids.push(id);
        }
      }
    }
    this.$scope.mentionIds = ids;
  };

  private removeMention = (id: string) => {
    const newMentionsArr: string[] = [];

    this.$scope.mentionsPreloaded.forEach((mention) => {
      if (mention !== id) {
        newMentionsArr.push(mention);
      }
    });

    this.$scope.mentionsPreloaded = newMentionsArr;
    // if a user is removed from the montionsPreloaded, but its name is in the form their avatar is placed in the mentionIds
    // and back into the list of people who will be notified
    this.onCommentChange(this.commentValue);
  };

  private removeAllMentions = () => {
    this.$scope.mentionsPreloaded = [];
  };

  private addMention = (ids: string[]) => {
    const clearIds = [];

    for (let i = 0; i < ids.length; i++) {
      if (ids[i] !== this.$scope.userId) {
        clearIds.push(ids[i]);
      }
    }

    this.$scope.mentionsPreloaded = this.$scope.mentionsPreloaded.concat(clearIds);
    this.$scope.mentionsPreloaded = this.$scope.mentionsPreloaded.filter((el, i, a) => i === a.indexOf(el));
  };

  private changeReason = (answer: string) => {
    this.$scope.nothingChangedReason = answer;
  };

  private changeUIMode = (uiMode: string) => {
    this.$scope.uiMode = uiMode;
  };

  private processMetric = (metric: IMetric) => {
    if (cachedUpdateMetricMap[currentMetricId] && !isCacheTimedout()) {
      metric.confidence = {
        value: cachedUpdateMetricMap[currentMetricId].confidence,
        reason: "",
      };
    } else {
      if (!metric.confidence) {
        metric.confidence = {
          value: 0.7 /** confidence values send/received from api should always be float */,
          reason: "",
        };
      }
    }

    this.$scope.checkin = {
      actual: metric.actual,
      confidence: metric.confidence.value,
      comment: "",
      checkInDate: new Date().toISOString(),
    };
  };

  private adjustDates = (session?: IPlanningSession): void => {
    this.$scope.checkin.checkInDate = new Date().toISOString();

    // used to save the initial value in the view
    this.$scope.currentDate = this.$scope.checkin.checkInDate;

    if (session) {
      this.$scope.datepickerOptions.minDate = new Date(session.start);
    }

    if (this.$scope.metric.dueDate.toString() !== "0001-01-01T00:00:00Z" && dayjs().diff(this.$scope.metric.dueDate, "days") >= 0) {
      this.$scope.uiMode = "changed";
      this.$scope.customCheckin = true;
      this.$scope.checkin.checkInDate = new Date(this.$scope.metric.dueDate).toISOString();
      this.$scope.datepickerOptions.maxDate = new Date(this.$scope.metric.dueDate);
    }
  };

  // we use this method to compare Dates and not strings
  private validateData = (firstDate = null, secondDate = null, strict = true, unit: dayjs.OpUnitType = "hours"): boolean => {
    // if there is no metric, retrun to prevent TypeError
    if (!this.$scope.metric) {
      return;
    }

    // if the function is called with parameters
    if (firstDate && secondDate) {
      const strictDifference = dayjs(new Date(secondDate)).diff(firstDate, "milliseconds");
      const notStrictDifference = dayjs(new Date(secondDate)).diff(firstDate, unit);

      return strict ? strictDifference >= 0 : notStrictDifference > 0;
    }

    if (this.sessionIsArchivedOrInTheFutureOrOkrIsAbandoned(unit)) {
      return false;
    }

    if (
      // there is not a dueDate
      (this.$scope.metric.dueDate === "0001-01-01T00:00:00Z" ||
        // or there is a dueDate and within the session time period
        (this.$scope.planningSession &&
          dayjs(new Date(this.$scope.planningSession.end)).diff(this.$scope.metric.dueDate, unit) >= 0 &&
          dayjs(new Date(this.$scope.metric.dueDate)).diff(this.$scope.planningSession.start, unit) > 0)) &&
      // and the current user is admin
      (this.isCurrentUserAdmin ||
        // or the current user is not admin but the hard deadline hasn't past yet
        (!this.isCurrentUserAdmin && dayjs(new Date(this.$scope.metric.dueDate)).diff(this.$scope.currentDate, unit) > 0) ||
        this.$scope.metric.dueDate === "0001-01-01T00:00:00Z")
    ) {
      return true;
    }

    return false;
  };

  private sessionIsArchivedOrInTheFutureOrOkrIsAbandoned = (unit: dayjs.OpUnitType): boolean => {
    return (
      dayjs(this.$scope.datepickerOptions.minDate).diff(this.$scope.currentDate, unit) > 0 ||
      (this.$scope.planningSession && this.$scope.planningSession.status === "closed") ||
      (this.$scope.metric.goalClosedStatus && this.$scope.metric.goalClosedStatus.status === "abandoned")
    );
  };

  private save = () => {
    this.$rootScope.traceAction("update_KR", () => {
      this.$scope.indicators.saving = true;

      if (this.$scope.uiMode === "nothingChanged" && this.$scope.nothingChangedReason !== "Other") {
        this.$scope.checkin.comment = this.$scope.nothingChangedReason + ". " + this.$scope.checkin.comment;
      }

      const checkin: ICheckin = {
        checkInDate: this.$scope.checkin.checkInDate,
        actual: parseFloat(`${this.$scope.checkin.actual}`),
        confidence: +this.$scope.checkin.confidence,
        comment: this.$scope.checkin.comment,
        gif: this.$scope.checkin.gif,
        changedBy: getCurrentUserId(),
        customFields: this.$scope.checkin.customFields,
      };

      this.metricService.checkin(this.$scope.metric, checkin).then(
        (newCheckin: ICheckin) => {
          this.postComment(newCheckin);
          this.$scope.indicators.saving = false;
          this.okrEvents.broadcastGoalsChanged("goalChanged");
          this.$rootScope.$broadcast("goalsChanged");

          this.okrEvents.broadcastGoalsChanged("metricUpdated", {
            checkin: newCheckin,
            metric: this.$scope.metric,
            goal: this.$scope.goal,
            session: this.$scope.planningSession,
          });

          this.okrEvents.broadcastGoalsChanged("metricSnapshotCreated", {
            checkin: newCheckin,
            metric: this.$scope.metric,
            goal: this.$scope.goal,
            session: this.$scope.planningSession,
          });

          this.$scope.$dismiss();
        },
        (err) => this.uiErrorHandlingService.handleIndicator(err, { indicator: "saving", inline: true })
      );
    });
  };

  private listAllMentionsInComment = (value: string): string[] => {
    let result = [];
    const ids = [];
    if (value) {
      const re = /@\[(.*?)\]/g;
      result = value.match(re);
    }
    if (result && result.length > 0) {
      for (let i = 0; i < result.length; i++) {
        const item = result[i].split(":")[2];
        const id = item.substring(0, item.length - 1);
        ids.push(id);
      }
    }
    return ids;
  };

  private removeDuplicatesFromList = (list: string[]): string[] => {
    const obj = list.reduce(function (acc, item) {
      acc[item] = true;
      return acc;
    }, {});

    return Object.keys(obj);
  };

  private removeMetricOwnerIdsFromMentions = (mentionedIds: string[]): string[] => {
    // metric has no owners => nothing to remove from mentionIds
    if (!this.$scope.metric.ownerIds.length) {
      return [];
    }

    return mentionedIds.filter((id) => !this.allUserOwnerIds.includes(id));
  };

  private mentionsCleanout = (ids: string[], commentText: string): string[] => {
    const noMetricOwnerIds = this.removeMetricOwnerIdsFromMentions(ids);

    // preserve metric/ goal owner ids if they are mentioned in comment
    const idsToBeMentioned: string[] = noMetricOwnerIds.concat(this.listAllMentionsInComment(commentText));

    return this.removeDuplicatesFromList(idsToBeMentioned);
  };

  private postComment = (checkin: ICheckin) => {
    this.$scope.mentionIds = this.$scope.mentionIds.concat(this.$scope.mentionsPreloaded);

    if (this.$scope.mentionIds.length > 0) {
      const comment: IComment = {
        mentioned: this.mentionsCleanout(this.$scope.mentionIds, this.$scope.checkin.comment),
        targetId: checkin.id,
        targetType: "checkinMention",
        targetTitle: this.$scope.metric.name,
        text: this.$scope.checkin.comment,
      };
      this.commentService.post(comment);
    }
  };

  private updateMetricComment = (checkinId: string, comment: string) => {
    this.$rootScope.traceAction("update_KR_comment", () => {
      this.$scope.indicators.saving = true;
      const checkinComment: ICheckinComment = {
        comment: comment,
        gif: this.$scope.checkin.gif,
      };

      // fake comment done only for the purposes of notifying users mentioned within the textarea (@mention) and KR assignee by email
      const mentionIds = this.$scope.mentionIds.concat(this.$scope.mentionsPreloaded);
      const fakeComment: IComment = {
        mentioned: this.mentionsCleanout(mentionIds, comment),
        targetId: checkinId,
        targetType: "checkinMention",
        targetTitle: this.$scope.metric.name,
        text: comment,
      };

      this.metricService.patchMetricComment(checkinId, checkinComment).then(
        () => {
          this.commentService.post(fakeComment);
          this.$scope.indicators.saving = false;

          this.okrEvents.broadcastGoalsChanged("metricCommentEdit", {
            metric: this.$scope.metric,
            goal: this.$scope.goal,
            session: this.$scope.planningSession,
          });

          this.$scope.$dismiss();
        },
        (err) => this.handleIndicatorError(err, "saving", false)
      );
    });
  };

  private updateBooleanMetric = () => {
    if (this.$scope.checkin.actual === this.$scope.metric.target) {
      this.$scope.checkin.actual = this.$scope.metric.initialValue;
    } else {
      this.$scope.checkin.actual = this.$scope.metric.target;
    }
  };

  private handleIndicatorError = <E>(err: E, indicator: string, inline = false): void => {
    this.uiErrorHandlingService.handleIndicator(err, { indicator, inline });
  };

  private updateCustomFields = (customFields: CfMap): void => {
    this.$scope.checkin.customFields = customFields;
  };

  private setRequiredCf = (requiredCf: { areFilled: boolean }): void => {
    this.$scope.requiredCfNotEmpty = requiredCf.areFilled;
  };
}
